import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Nullable, PrismicLink } from 'corgi-types';
import { color } from 'styled-system';

import { CssGrid } from 'common/components/CssGrid';
import RichContent from 'common/components/RichText/RichContent';
import { breakpoint } from 'common/theme/helper';
import { TextWithIcon } from './TextWithIcon';

interface ShowroomOutletHeadingProps {
  location: Nullable<string>;
  openingHours: Nullable<string>;
  phone: Nullable<string>;
  locationLink: Nullable<PrismicLink>;
  instagramLink: Nullable<PrismicLink>;
  instagramLabel: Nullable<string>;
  extraInfo: Nullable<string>;
}

const PLACEHOLDER_TEXT = '-';

const gridStyles = {
  gridAutoColumns: 'minmax(min-content, max-content)',
  justifyContent: ['flex-start', 'center'],
};

const UnstyledPhone = styled(TextWithIcon)<any>`
  -ms-grid-row: 2;
  ${breakpoint(2)} {
    -ms-grid-column: 2;
    -ms-grid-row: 1;
  }
`;

const Phone = (props: ComponentProps<typeof UnstyledPhone>) => (
  <UnstyledPhone gridColumn={['1', null, null, '2']} iconName="phone" {...props} />
);

const UnstyledOpeningHours = styled(TextWithIcon)<any>`
  -ms-grid-row: 3;
  ${breakpoint(0)} {
    -ms-grid-column: 2;
    -ms-grid-row: 1;
  }
  ${breakpoint(2)} {
    -ms-grid-column: 3;
  }
`;

const OpeningHours = (props: ComponentProps<typeof UnstyledOpeningHours>) => (
  <UnstyledOpeningHours
    gridColumn={['1', '2', null, '3']}
    gridRow={['3', '1']}
    pt={['sp_20', 'sp_0']}
    pr={[null, null, null, 'sp_24']}
    iconName="clock"
    {...props}
  />
);

const UnstyledInstagram = styled(TextWithIcon)<any>`
  -ms-grid-row: 4;
  ${breakpoint(0)} {
    -ms-grid-column: 2;
    -ms-grid-row: 2;
  }
  ${breakpoint(2)} {
    -ms-grid-column: 4;
    -ms-grid-row: 1;
  }
`;

const Instagram = (props: ComponentProps<typeof UnstyledInstagram>) => (
  <UnstyledInstagram
    gridColumn={['1', '2', null, '4']}
    px="sp_0"
    iconName="instagramFlat"
    {...props}
  />
);

const UnstyledExtraInfo = styled(TextWithIcon)``;

const ExtraInfo = (props: ComponentProps<typeof UnstyledExtraInfo>) => (
  <UnstyledExtraInfo
    justifyContent={['flex-start', 'center']}
    pt={['sp_20', null, null, 'sp_24']}
    px="sp_0"
    maxWidth="100%"
    {...props}
  />
);

const UnstyledStyledRichContent = styled(RichContent)<any>`
  ${color};
`;

const StyledRichContent = (props: ComponentProps<typeof UnstyledStyledRichContent>) => (
  <UnstyledStyledRichContent color="sys.primary.background.default" {...props} />
);

const ShowroomOutletHeading = ({
  location,
  openingHours,
  phone,
  locationLink,
  instagramLink,
  instagramLabel,
  extraInfo,
}: ShowroomOutletHeadingProps) => (
  <div data-corgi-id="showroom-outlet-heading">
    <CssGrid {...gridStyles}>
      <TextWithIcon iconName="location" link={locationLink} pt="sp_0" withSeparator={true}>
        {location || PLACEHOLDER_TEXT}
      </TextWithIcon>
      <Phone
        withSeparator={true}
        {...(phone
          ? {
              link: { url: `tel:${phone.replace(/\s/g, '')}`, target: null },
            }
          : {})}
      >
        {phone || PLACEHOLDER_TEXT}
      </Phone>
      <OpeningHours withSeparator={true}>{openingHours || PLACEHOLDER_TEXT}</OpeningHours>
      <Instagram link={instagramLink}>{instagramLabel || PLACEHOLDER_TEXT}</Instagram>
    </CssGrid>
    {extraInfo && (
      <ExtraInfo iconName="extraInfo">
        <StyledRichContent content={extraInfo} />
      </ExtraInfo>
    )}
  </div>
);

export default ShowroomOutletHeading;
